import { Button, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ObjectSchema } from 'yup';
import { uiStateSelectors } from '../../../services/redux/features/uiState.slice';
import { MixedComponentsProps } from '../../../types/mixedComponentProps';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from './formInputs';
import { ContactDetailsFormNewModel } from './formModel';
import { contactDetailsFormNewInitialValues } from './initialValues';
import { contactDetailsFormSchema } from './validationSchema';

export const ContactDetailsFormNew_MX: React.FC<MixedComponentsProps> = ({
  formHelpers,
  titleTranslationKey = 'contactDetailsForm:title:mixed',
  onValuesChange,
}) => {
  const [initialValues] = useState(contactDetailsFormNewInitialValues);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<Partial<ContactDetailsFormNewModel>>>();

  useEffect(() => {
    setValidationSchema(contactDetailsFormSchema());
  }, []);

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (values: ContactDetailsFormNewModel) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(titleTranslationKey), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent
          onFormInitialValuesChange={onFormInitialValuesChange}
          disabled={isDisabledFormSummary}
          initialFormValue={initialValues}
          onValuesChange={onValuesChange}
          formHelpers={formHelpers}
        />
      </Formik>
    </FormSection>
  );
};

export const FormContent = (props) => {
  const { values, submitForm } = useFormikContext<ContactDetailsFormNewModel>();
  const { disabled, onValuesChange, formHelpers } = props;
  const { onFormValuesChange } = formHelpers;

  useEffect(() => {
    onValuesChange?.(values);
    onFormValuesChange(values);
  }, [values, onValuesChange, onFormValuesChange]);

  const snCSubmitted = !useSelector(uiStateSelectors.snCSubmittedMock);
  const disableAllFormControls = disabled || snCSubmitted;

  const [inputs, updateInputs] = useState<ReturnType<typeof createFormInputs>>(() =>
    createFormInputs(disableAllFormControls)
  );

  useEffect(() => {
    updateInputs(createFormInputs(disableAllFormControls));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableAllFormControls]);

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
      <>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3">
              <Button
                type="button"
                secondary
                icon="address-book"
                ariaLabel="Search existing customer"
                onClick={() => ''}
              >
                {t('contactDetailsForm:searchExistingCustomer')}
              </Button>
            </Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">
              {inputs.title()}
            </Layout.Item>
            <Layout.Item default="1/3" s="1/1">
              {inputs.firstName()}
            </Layout.Item>
            <Layout.Item default="1/3" s="1/1">
              {inputs.lastName()}
            </Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">
              {inputs.dateOfBirth()}
            </Layout.Item>
            <Layout.Item default="1/3" s="1/1">
              {inputs.email()}
            </Layout.Item>
            <Layout.Item default="1/3" s="1/1">
              {inputs.invoiceEmailAddress()}
            </Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">
              {inputs.countryCode()}
            </Layout.Item>
            <Layout.Item default="1/3" s="1/1">
              {inputs.mobileNumber()}
            </Layout.Item>
            <Layout.Item default="1/3" s="1/1">
              {inputs.correspondenceMethod()}
            </Layout.Item>
          </Layout>
        </Fieldset.Row>
        <FormSectionSubmitButton  onClick={submitForm} formHelpers={formHelpers}/>
      </>
    );
};
